import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import { useTranslation } from 'react-i18next';
import LoadingGrid from 'routes/HumanResourcesModule/components/LoadingGrid';
import { ColDef, GetRowIdParams } from 'ag-grid-community';
import { StyGridContainer } from 'routes/HumanResourcesModule/components/ui/StyGridContainer';
import { useGetHrGradeMappings } from 'routes/HumanResourcesModule/Settings/HrGrade/helpers/hooks/gradeMapping';
import { getColDefs } from 'routes/HumanResourcesModule/Settings/HrGrade/helpers/colDefinitions';
import ActionsCellRenderer from 'routes/HumanResourcesModule/Settings/HrGrade/components/ActionsCellRenderer';
import TooltipCellRenderer from 'routes/BudgetModule/components/BudgetOverview/BudgetSynthesisAndRatios/BudgetBalance/TooltipCellRenderer';
import GradeMappingInfoAlert from 'routes/HumanResourcesModule/Settings/HrGrade/components/GradeMappingInfoAlert';
import HrCard from 'routes/HumanResourcesModule/components/HrCard';
import _ from 'lodash';
import FullScreenProvider from 'contexts/FullScreenContextProvider';
import ConditionnalZoomIconButton from 'components/ConditionnalZoomIconButton';
import { TRACKABLE_EVENTS_METADATA } from 'helpers/trackerHelpers';
import { HUMAN_RESOURCES } from 'business/constants';

const CustomGridContainer = styled(StyGridContainer, {
    name: 'CustomGridContainer',
})({
    // remove default grid container columns border style
    '&&&  .ag-header-group-cell-with-group, .ag-header-cell': {
        borderLeft: 'none !important',
    },
    '&&& .ag-cell': {
        borderRight: 'none !important',
    },
    height: '100%',
    width: '100%',
});

const StyPayStateCard = styled(HrCard)(() => ({
    '&&& .MuiCardHeader-action': { mr: 0 },
}));

export default function HrGradeGrid(): React.ReactElement {
    const { t } = useTranslation();

    const { data: gradeMappingsData, isLoading: areGradeMappingsLoading } =
        useGetHrGradeMappings();

    const gradeMappings =
        gradeMappingsData?.map((grade) => {
            return {
                ...grade,
                nonOfficialFiliere: _.capitalize(grade.nonOfficialFiliere),
                nonOfficialGrade: _.capitalize(grade.nonOfficialGrade),
            };
        }) || [];

    const columnDefs: ColDef[] = useMemo(() => getColDefs(t), []);

    const [isFullScreen, setIsFullScreen] = useState(false);
    const getRowId = useCallback(
        (params: GetRowIdParams) => String(params.data.id),
        [],
    );

    const [showAlert, setShowAlert] = useState(false);

    useEffect(() => {
        setShowAlert(
            gradeMappings.some((grade) => grade.officialGrade === null),
        );
    }, [JSON.stringify(gradeMappings)]);

    if (areGradeMappingsLoading) {
        return <LoadingGrid />;
    }

    return (
        <FullScreenProvider
            module={HUMAN_RESOURCES}
            trackEventMetadata={{
                gridName:
                    TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                        .gradeMappingGrid,
            }}
        >
            <StyPayStateCard
                title={t('humanResources.settings.gradeMapping.gridTitle')}
                startAdornment={<ConditionnalZoomIconButton />}
                cardHeaderActions={<GradeMappingInfoAlert show={showAlert} />}
                setIsFullScreen={setIsFullScreen}
            >
                <CustomGridContainer>
                    <AgGridReact
                        className="ag-theme-material"
                        domLayout={isFullScreen ? 'normal' : 'autoHeight'}
                        suppressRowClickSelection
                        rowData={gradeMappings}
                        columnDefs={columnDefs}
                        rowHeight={32}
                        headerHeight={36}
                        getRowId={getRowId}
                        pagination={!isFullScreen}
                        paginationPageSize={10}
                        components={{
                            textFloatingFilter: TextFloatingFilter,
                            actionsCellRenderer: ActionsCellRenderer,
                            tooltipCellRenderer: TooltipCellRenderer,
                        }}
                    />
                </CustomGridContainer>
            </StyPayStateCard>
        </FullScreenProvider>
    );
}
